/**
 * Tile — one cell of the wall. Exhibits, never annotates (BRIEF §2): nothing at rest but the
 * picture, or the user's own latest take on that basis (§8). Doors appear only on hover (desktop)
 * — the words as chips (board `WordsAsDoors`, "the hover label is cut into the same chips"). A cell
 * offers exactly one gesture, arming (`onArm`) — no cell fires a generation straight off itself; see
 * "The tile carries no generation verb" below. Toile has no pencil: the direction field is always on
 * in the column. While a take is queued or rendering on this cell's chain, it breathes — see "The
 * cell at work" below for the shape that took over 2026-09-12.
 *
 * **The hover doors, as words** (board `WordsAsDoors`, validated 2026-09-18). `words` is the
 * chain's own doors (`chains.captionDoors` — a tile's `chips`, or a take's typed direction, cut);
 * `[]` renders `label` as plain text instead — not a door, not a link (a chip-less tile, e.g. every
 * Ovid tile today). A word tapped here sets the wall's query WITHOUT arming (`onWordTap`); a
 * narrow cell drops a whole chip rather than cutting one mid-word, in CSS alone — `.tileChips`
 * (`Tile.module.scss`) bounds `WordDoors`' own row to the cell's width, where its wrap-and-clip
 * takes over (`WordDoors.tsx`'s own header doc has the full rationale).
 *
 * **The pack-page link is focus-only** (operator, 2026-09-19: "no pack link on mouse hover" — a
 * mouse hover reveals only the words, never the ↗). It stays in the DOM and in the server markup
 * always — a crawler's own route to the pack page never depended on visibility — but is invisible
 * and pointer-inert until keyboard focus lands anywhere in the cell, like a skip link
 * (`WordDoors`' `arrowReveal: 'focus'`, raised back by `.tile:focus-within` below). `pageLinkLabel`
 * (the pack's own title, not the compound `label`) names the actual page a screen-reader user would
 * land on; omitted, `WordDoors`' own generic door name stands.
 *
 * **The tile carries no generation verb (decided 2026-09-01, extended to every cell on the
 * operator's second look, 2026-09-19)**: a wall_tiles cell used to fire an immediate `Again` from
 * its hover door's Generate button; a from-scratch cell kept that same door as an exception.
 * Tapping the picture already only armed (`onArm`, BRIEF §5's "click on the picture = armed,
 * nothing fired"); the Generate door was a second, redundant way to spend, and firing straight off
 * the tile skipped the bar the second, deliberate tap is meant to be — the same reasoning applies
 * equally to a from-scratch cell, so the exception is gone with it. Arming is now the ONLY gesture
 * ANY cell offers: `Wall.tsx` passes no `onGenerateNow` to any `<Tile>` any more, and this
 * component renders no Generate control, ever. No theme word replaces the removed button —
 * `HouseTheme.verbs` (`ui/theme.ts`) has no "arm" word to hover-label the picture with, so none is
 * shown; the picture's own `aria-label` already names the cell for assistive tech.
 *
 * Structure, per the review's accessibility note: ONE accessible name for the cell — the picture
 * button's, which is the quick look (BRIEF §5) — and the interactive things are siblings, never
 * nested: a plain `<figure>` holds the picture button and the doors (chips, or plain text for a
 * chip-less/from-scratch cell). The doors are not tabbable until the cell is hovered or holds
 * focus, so a keyboard walk down the wall steps through cells, not through per-cell controls.
 *
 * **The cell at work — row G, "three signs, one motion"** (`thinking/design/toile_wall/WorkingStates.dc.html`,
 * row G; supersedes row F's thread-and-caret scheme, operator-validated 2026-09-12). `working`
 * stays ONE state on the wire (unchanged above) — `queuePlace` and `previewFrame` are still what
 * paint it, but row G asks only two questions, not six:
 * - **Is it alive?** The breath (`.breath`) — shared by every unfinished cell, in phase, and it
 *   does not stop until a frame lands. Queued and claimed read identically here; the breath alone
 *   never distinguishes them, by design.
 * - **Is mine coming?** The frames, and only the frames (`previewFrame`): once a frame exists it
 *   replaces the breath outright, as the service made it (no filter, no ramp — a picture emerging
 *   is its own progress, the softness is not the point).
 * Between the two sits the **place** — the cell's only text, sized as an edition number
 * (`.numeral`) so a phone can read it, `--tl-dim` and `--tl-text` at place 1. **Claimed is not a
 * viewer state** (row G's own text): "you are next" and "you are on the machine" are the same fact
 * from the chair: `Wall.tsx` hands a `rendering` row place 1 and numbers the queued rows on from
 * there, so the numeral holds through the claimed gap and the first frame replaces it.
 * Tungsten appears once more, after the picture: a hairline the beat the result lands
 * (`transitionsToDone` below), then rest.
 *
 * **Removed from row F**: the searching/settling thread and its determinate widths, the blur-and-
 * scale ramp across frames 1–3, the tungsten numeral at place 1 (place 1 is `--tl-text` now, not
 * `--tl-accent`), and every branch keyed on "claimed" as its own paint. Reduced motion needs no
 * separate rule for any of row G's marks: the breath's existing color-pulse variant is untouched,
 * the numeral and frames carry no animation to park, and the done-transition hairline is gated by
 * a fixed-duration mount, not a CSS animation, so it already behaves the same under either
 * preference.
 *
 * **The breath/numeral/frame layers themselves live in `WorkingCell.tsx`** (2026-09-12 carrier
 * fix) — one shared definition the armed view's result slot (`Column.tsx`, `Sheet.tsx`) renders
 * too, so a tile and the row it is making read the same while working. This file keeps only what
 * is genuinely tile-only: the done-transition hairline below, and the `rest`/`done` picture.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import {WorkingCell, type PreviewFrame} from './WorkingCell'
import {WordDoors} from './WordDoors'
import type {Door} from './chains'
import type {HouseDoor} from './theme'
import styles from './Tile.module.scss'

export type TileVisualState = 'rest' | 'working' | 'done'

/** True exactly the beat a cell lands on `done` straight out of `working` — never for a cell that
 *  is already `done` on its very first render (there is no `prev` to compare against a mount, so a
 *  page load or a catalog scroll never flashes) and never for `rest` → `done` (the tile never
 *  actually showed the work happening). Kept as a pure function, exported, so the one fact the
 *  done-transition hairline is gated on is checkable without mounting anything — this file's own
 *  spec (`__tests__/Tile.spec.tsx`) uses `renderToStaticMarkup`, a single pass with no jsdom and no
 *  re-renders, so a real prop-to-prop transition cannot be driven through the component itself. */
export function transitionsToDone(prev: TileVisualState, next: TileVisualState): boolean {
    return prev === 'working' && next === 'done'
}

/** How long the done-transition hairline stays mounted, in ms (row G: "one beat, ~1.2s"). */
const DONE_FLASH_MS = 1200

/** Whether the device can hover at all — read once, client-side. Without a hovering pointer the
 *  mouse events below are Safari's tap emulation, and a tap must arm, not open the doors (the
 *  `Tile.module.scss` note states the mechanism). Server render: no hover, no handlers — the
 *  hydrating render agrees, since the handlers carry no markup. */
const CAN_HOVER =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover)').matches

export type TileProps = {
    /** The picture at rest, or the chain's newest take once one exists. Null on a from-scratch
     *  cell whose own root is still rendering — the cell shows its ground, nothing else. */
    imageUrl: string | null
    /** The cell's whole accessible name (the picture button's `aria-label`) and the plain-text
     *  fallback the doors row shows when `words` is empty — always a full description, independent
     *  of what the row actually draws. */
    label: string
    /** The doors (`chains.captionDoors`) — `[]` renders `label` as plain text instead (a chip-less
     *  tile, or a from-scratch/pinned cell with nothing typed yet). */
    words?: readonly Door[]
    /** A door tap: sets the wall's query WITHOUT arming (`Wall.tsx`). Required whenever `words` is
     *  non-empty — the chips it renders have nowhere else to call. */
    onWordTap?: (door: Door) => void
    /** The pack page behind the label; `null` for a from-scratch cell (there is no page) or a
     *  chip-less one (rendered as plain text, per `words` above — no link either). */
    labelHref: string | null
    /** The pack's own title, for the focus-revealed ↗'s accessible name — never the compound
     *  `label` (which also carries the chip words). `undefined` where `labelHref` is null anyway
     *  (no link renders, so nothing needs naming). */
    pageLinkLabel?: string
    state: TileVisualState
    /** 1-based place in the user's own line (`Wall.tsx` derives it off `mine.pending`: the
     *  `rendering` row is 1, the `queued` rows count on, `createdAt` then `position`). Set while
     *  `state === 'working'`; `null`/absent while merely firing or once the row is `done`. */
    queuePlace?: number | null
    /** The rendering row's newest loaded frame (`usePreviewFrames.ts`). `null`/absent before frame
     *  1 has loaded and once the row is `done`. Mutually exclusive with `queuePlace`. */
    previewFrame?: PreviewFrame | null
    /** Aspect ratio from stored width/height — the layout has already sized the cell. */
    width: number
    height: number
    /** Before the JS measures, only the first row loads eagerly. Once measured, every rendered cell
     *  does: the wall's own windowing is the lazy mechanism, and `loading="lazy"` on top of it
     *  deferred a second time with the browser's own small margin (Safari) — rows already fetched
     *  painted late (2026-09-23). */
    eager?: boolean
    /** A visitor's door, in a house that closes itself (`HouseTheme.visitor.anonDoor`): the shade
     *  the cell already draws on hover/focus, plus at most two words layered over it. It REPLACES
     *  the label rather than joining it — a cell that carries a door carries no pack page to link
     *  to and no estate text to show — and it is `aria-hidden` presentation: the whole cell stays
     *  one button, named by `label`, and the door never takes the pointer from it. */
    door?: HouseDoor | null
    onArm: () => void
}

export function Tile({
    imageUrl, label, words = [], onWordTap, labelHref, pageLinkLabel, state,
    queuePlace, previewFrame, width, height, eager, door, onArm,
}: TileProps) {
    // The doors open on hover OR on focus anywhere inside the cell, and only then do they take tab
    // stops. The cell itself is the first stop, so a keyboard user reaches it, and the doors become
    // reachable from there.
    const [active, setActive] = useState(false)
    const doorTabIndex = active ? 0 : -1

    // The fade belongs to the picture arriving, not to the element mounting: a cached image is
    // already complete on the first commit (`img.complete`), and a slow one must not fade in over
    // an empty box before it has any pixels.
    const imgRef = useRef<HTMLImageElement>(null)
    const [loaded, setLoaded] = useState(false)
    const onLoad = useCallback(() => setLoaded(true), [])
    useEffect(() => {
        setLoaded(imgRef.current?.complete === true)
    }, [imageUrl])

    // The done-transition hairline (row G): a tungsten beat fired exactly on `working` → `done`,
    // never on mount. `prevStateRef` is read once per commit, before being overwritten, so the
    // comparison always holds the PREVIOUS render's state against this one's.
    const prevStateRef = useRef(state)
    const [justFinished, setJustFinished] = useState(false)
    useEffect(() => {
        const prev = prevStateRef.current
        prevStateRef.current = state
        if (transitionsToDone(prev, state)) {
            setJustFinished(true)
            const timer = setTimeout(() => setJustFinished(false), DONE_FLASH_MS)
            return () => clearTimeout(timer)
        }
        return undefined
    }, [state])

    return (
        <figure
            className={styles.tile}
            style={{aspectRatio: `${width} / ${height}`}}
            onMouseEnter={CAN_HOVER ? () => setActive(true) : undefined}
            onMouseLeave={CAN_HOVER ? () => setActive(false) : undefined}
            onFocus={() => setActive(true)}
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setActive(false)
            }}
        >
            {state === 'working' ? (
                <WorkingCell queuePlace={queuePlace} previewFrame={previewFrame} />
            ) : imageUrl ? (
                <img
                    ref={imgRef}
                    src={imageUrl}
                    // Pack-titled cell (`labelHref` set): the picture's alt names it for image search
                    // and reader modes — the same text the hover door and the picture button's own
                    // `aria-label` already carry, so no new name is invented here (SEO fix round,
                    // 2026-09-01). A from-scratch cell has no pack behind it, so `label` there is a
                    // typed direction or the "Make one" verb, not a title — alt stays empty and the
                    // picture stays decorative, as it was.
                    alt={labelHref ? label : ''}
                    loading={eager ? 'eager' : 'lazy'}
                    decoding="async"
                    onLoad={onLoad}
                    className={loaded ? `${styles.image} ${styles.imageIn}` : styles.image}
                />
            ) : null}

            {/* The done-transition hairline — tungsten, one beat, gone. Never present for a cell
                that was already done when it mounted. */}
            {justFinished && <span className={styles.hairline} aria-hidden="true" />}

            <button type="button" className={styles.picture} onClick={onArm} aria-label={label} />

            {state !== 'working' && (door
                ? (
                    <div className={styles.visitorDoor} aria-hidden="true">
                        {door.line !== null && <span className={styles.visitorLine}>{door.line}</span>}
                        <span className={styles.visitorCta}>{door.cta}</span>
                    </div>
                )
                : (
                    <div className={styles.doors}>
                        <div className={styles.wordsGroup}>
                            {words.length > 0 ? (
                                <WordDoors
                                    doors={words}
                                    onTap={(door) => onWordTap?.(door)}
                                    href={labelHref}
                                    tabIndex={doorTabIndex}
                                    className={styles.tileChips}
                                    arrowReveal="focus"
                                    arrowLabel={pageLinkLabel ? `Open the pack page: ${pageLinkLabel}` : undefined}
                                />
                            ) : (
                                <span className={styles.plain}>{label}</span>
                            )}
                        </div>
                    </div>
                )
            )}
        </figure>
    )
}
